/**
 * Utility functions for formatting data
 */

/**
 * Get currency symbol from user preferences or default to $
 */
export const getCurrencySymbol = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.currency_symbol || '$';
  } catch {
    return '$';
  }
};

/**
 * Format amount with currency symbol (simple format without locale)
 * Use this for compact displays like charts
 */
export const formatAmount = (amount, options = {}) => {
  const symbol = getCurrencySymbol();
  const { compact = false, decimals = 2 } = options;
  
  if (compact && Math.abs(amount) >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(1)}k`;
  }
  
  return `${symbol}${Number(amount).toFixed(decimals)}`;
};

/**
 * Format amount with full locale formatting
 * Use this for detailed displays
 */
export const formatAmountLocale = (amount, options = {}) => {
  const symbol = getCurrencySymbol();
  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  
  const formatted = Number(amount).toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits
  });
  
  return `${symbol}${formatted}`;
};

/**
 * Format bytes to human readable string
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format date to locale string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString(undefined, options);
};

/**
 * Format date and time to locale string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
};

/**
 * Format currency (legacy - uses Intl)
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);
};

/**
 * Get frequency label from key
 */
export const getFrequencyLabel = (frequency) => {
  const labels = {
    'weekly': 'Weekly',
    'biweekly': 'Bi-Weekly',
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'annually': 'Annually',
    'one-time': 'One-Time'
  };
  return labels[frequency] || frequency;
};

/**
 * Get category color
 */
export const getCategoryColor = (category) => {
  const colors = {
    'utilities': '#3b82f6',
    'rent': '#8b5cf6',
    'insurance': '#ec4899',
    'subscriptions': '#f59e0b',
    'loans': '#ef4444',
    'other': '#6b7280'
  };
  return colors[category] || colors.other;
};

/**
 * Get account type icon emoji
 */
export const getAccountTypeIcon = (type) => {
  const icons = {
    'checking': '🏛️',
    'savings': '💰',
    'credit_card': '💳',
    'cash': '💵',
    'other': '💼'
  };
  return icons[type] || icons.other;
};

/**
 * Get account type label from key
 */
export const getAccountTypeLabel = (type) => {
  if (!type) return '';
  return type.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Get user initials from name
 */
export const getInitials = (firstName, lastName) => {
  const first = firstName?.[0] || '';
  const last = lastName?.[0] || '';
  return (first + last).toUpperCase();
};

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
export const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};
